/**
 * Cart Groups
 *
 * Visually groups cart line items that share the same hidden
 * _group_id property (set by the jewelry configurator).
 *
 * Features:
 * - Wraps grouped items in a visual container with a header
 * - Adds a single "remove group" button that removes all items
 * - Adds a group quantity control that syncs all items in the group
 * - Re-applies grouping after AJAX cart updates
 */

(function () {
  "use strict";

  const GROUP_PROP = "_group_id";
  const GROUP_TITLE_PROP = "_group_title";
  let cartData = null;
  let processing = false;
  let initializing = false;

  /* ------------------------------------------------------------------ */
  /*  Cart API helpers                                                   */
  /* ------------------------------------------------------------------ */

  async function fetchCart() {
    const resp = await fetch("/cart.js", { credentials: "same-origin" });
    cartData = await resp.json();
    return cartData;
  }

  async function updateCart(updates) {
    const resp = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ updates }),
    });
    return resp.json();
  }

  /* ------------------------------------------------------------------ */
  /*  Build group map from cart data                                     */
  /* ------------------------------------------------------------------ */

  function buildGroups(cart) {
    const groups = new Map(); // groupId → { title, items: CartItem[] }

    for (const item of cart.items) {
      const groupId = item.properties?.[GROUP_PROP];
      if (!groupId) continue;

      if (!groups.has(groupId)) {
        groups.set(groupId, {
          title: item.properties?.[GROUP_TITLE_PROP] || "Konfiguration",
          items: [],
        });
      }
      groups.get(groupId).items.push(item);
    }

    return groups;
  }

  /* ------------------------------------------------------------------ */
  /*  DOM manipulation – wrap grouped items                              */
  /* ------------------------------------------------------------------ */

  function findCartItemElement(itemKey, container) {
    // The theme uses data-merge-list-item="item-key:KEY|..." on wrapper divs
    const els = container.querySelectorAll("[data-merge-list-item]");
    for (const el of els) {
      const attr = el.getAttribute("data-merge-list-item");
      if (attr && attr.startsWith("item-key:" + itemKey)) return el;
    }
    // Fallback: find by quantity input data-key
    const input = container.querySelector(`input[data-key="${itemKey}"]`);
    if (input)
      return (
        input.closest("[data-merge-list-item]") ||
        input.closest(".cart-item")?.parentElement
      );
    return null;
  }

  function formatPrice(cents) {
    return (cents / 100).toLocaleString("de-DE", {
      style: "currency",
      currency: "EUR",
    });
  }

  function applyGrouping(container) {
    if (!cartData) return;
    const groups = buildGroups(cartData);
    if (groups.size === 0) return;

    // Remove previously injected group wrappers — restore items to DOM first,
    // otherwise removing the wrapper would also remove the items inside it.
    container.querySelectorAll(".cart-group-wrapper").forEach((wrapper) => {
      const itemsContainer = wrapper.querySelector(".cart-group__items");
      if (itemsContainer) {
        while (itemsContainer.firstChild) {
          wrapper.parentNode.insertBefore(itemsContainer.firstChild, wrapper);
        }
      }
      wrapper.remove();
    });

    // Track which item elements belong to groups
    const groupedElements = new Map(); // groupId → HTMLElement[]

    for (const [groupId, group] of groups) {
      const elements = [];
      for (const item of group.items) {
        const el = findCartItemElement(item.key, container);
        if (el) elements.push({ el, item });
      }
      if (elements.length === 0) continue;
      groupedElements.set(groupId, elements);
    }

    // For each group, create a wrapper and move elements into it
    for (const [groupId, elements] of groupedElements) {
      const group = groups.get(groupId);
      const groupQuantity = elements[0]?.item.quantity || 1;
      const groupTotal = group.items.reduce(
        (sum, it) => sum + it.final_line_price,
        0,
      );

      // Create group wrapper — summary row uses the same cart-item column classes
      // so it inherits the theme's grid layout and aligns with regular items.
      const wrapper = document.createElement("div");
      wrapper.className = "cart-group-wrapper";
      wrapper.dataset.groupId = groupId;
      wrapper.innerHTML = `
        <div class="cart-item cart-group__summary-row">
          <div class="cart-item__column cart-item__image">
            <div class="cart-group__icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
              </svg>
            </div>
          </div>
          <div class="cart-item__not-image">
            <div class="cart-item__column cart-item__description">
              <div class="lightly-spaced-row">
                <div class="cart-item__title">${group.title}</div>
                <button type="button" class="cart-group__toggle small-text" data-group-toggle="${groupId}" aria-expanded="false" aria-controls="cart-group-details-${groupId}">
                  ${elements.length} Artikel
                  <svg class="cart-group__chevron" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
              </div>
              <ul class="cart-group__detail-list" id="cart-group-details-${groupId}" hidden>
                ${group.items
                  .map((item) => {
                    const variantLabel =
                      item.variant_title &&
                      item.variant_title !== "Default Title"
                        ? ` &ndash; ${item.variant_title}`
                        : "";
                    return `<li class="cart-group__detail-item">
                    <span class="cart-group__detail-name">${item.product_title}${variantLabel}</span>
                    <span class="cart-group__detail-price">${formatPrice(item.final_price)}</span>
                  </li>`;
                  })
                  .join("")}
              </ul>
            </div>
            <div class="cart-item__column cart-item__price">
              <span class="theme-money cart-item__selling-price">${formatPrice(groupTotal / groupQuantity)}</span>
            </div>
            <div class="cart-item__column cart-item__quantity">
              <div class="quantity buttoned-input">
                <button type="button" class="quantity-down cart-group__qty-btn" data-group-qty-action="decrease" data-group-id="${groupId}" aria-label="Menge verringern">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-minus" aria-hidden="true" focusable="false" role="presentation"><path d="M5 12h14"/></svg>
                </button>
                <input type="number" class="cart-group__qty-input cart-item__quantity-input" value="${groupQuantity}" min="0" data-group-id="${groupId}" aria-label="Menge" />
                <button type="button" class="quantity-up cart-group__qty-btn" data-group-qty-action="increase" data-group-id="${groupId}" aria-label="Menge erhöhen">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-plus" aria-hidden="true" focusable="false" role="presentation"><path d="M12 5v14M5 12h14"/></svg>
                </button>
              </div>
              <button type="button" class="cart-item__remove cart-group__remove small-text underline" data-group-remove="${groupId}">
                L&ouml;schen
              </button>
            </div>
            <div class="cart-item__column cart-item__total">
              <span class="theme-money cart-item__selling-total">${formatPrice(groupTotal)}</span>
            </div>
          </div>
        </div>
        <div class="cart-group__items" hidden></div>
      `;

      // Insert wrapper before the first element of the group
      const firstEl = elements[0].el;
      firstEl.parentNode.insertBefore(wrapper, firstEl);

      // Move all group item elements into the hidden items container.
      // They stay in the DOM so form inputs (updates[]) still submit correctly,
      // but they are not visible — the compact detail list shows the info instead.
      const itemsContainer = wrapper.querySelector(".cart-group__items");
      for (const { el } of elements) {
        itemsContainer.appendChild(el);
      }
    }

    // Bind group control events
    bindGroupEvents(container);
  }

  /* ------------------------------------------------------------------ */
  /*  Group event handlers                                               */
  /* ------------------------------------------------------------------ */

  function bindGroupEvents(container) {
    // Detail list toggle
    container.querySelectorAll("[data-group-toggle]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const groupId = btn.dataset.groupToggle;
        const list = container.querySelector(`#cart-group-details-${groupId}`);
        if (!list) return;
        const expanded = btn.getAttribute("aria-expanded") === "true";
        btn.setAttribute("aria-expanded", String(!expanded));
        list.hidden = expanded;
      });
    });

    // Remove group
    container.querySelectorAll("[data-group-remove]").forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        e.preventDefault();
        if (processing) return;
        processing = true;

        const groupId = btn.dataset.groupRemove;
        const group = buildGroups(cartData).get(groupId);
        if (!group) {
          processing = false;
          return;
        }

        const updates = {};
        for (const item of group.items) {
          updates[item.key] = 0;
        }

        await updateCart(updates);
        await refreshCartUI();
        processing = false;
      });
    });

    // Quantity buttons
    container.querySelectorAll("[data-group-qty-action]").forEach((btn) => {
      btn.addEventListener("click", async (e) => {
        e.preventDefault();
        if (processing) return;
        processing = true;

        const groupId = btn.dataset.groupId;
        const action = btn.dataset.groupQtyAction;
        const group = buildGroups(cartData).get(groupId);
        if (!group) {
          processing = false;
          return;
        }

        const currentQty = group.items[0]?.quantity || 1;
        const newQty =
          action === "increase" ? currentQty + 1 : Math.max(0, currentQty - 1);

        const updates = {};
        for (const item of group.items) {
          updates[item.key] = newQty;
        }

        await updateCart(updates);
        await refreshCartUI();
        processing = false;
      });
    });

    // Direct quantity input change
    container.querySelectorAll(".cart-group__qty-input").forEach((input) => {
      input.addEventListener("change", async () => {
        if (processing) return;
        processing = true;

        const groupId = input.dataset.groupId;
        const group = buildGroups(cartData).get(groupId);
        if (!group) {
          processing = false;
          return;
        }

        const newQty = Math.max(0, parseInt(input.value) || 0);
        const updates = {};
        for (const item of group.items) {
          updates[item.key] = newQty;
        }

        await updateCart(updates);
        await refreshCartUI();
        processing = false;
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Refresh the cart UI (triggers theme's section re-render)           */
  /* ------------------------------------------------------------------ */

  async function refreshCartUI() {
    // Re-fetch cart data
    await fetchCart();

    // Trigger the theme's cart drawer refresh
    document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));

    // For the cart page: trigger a full section re-render
    const cartForm = document.querySelector("cart-form[data-ajax-update]");
    if (cartForm?.refresh) {
      cartForm.refresh();
    }

    // init() will be triggered by the dispatch:cart-drawer:refresh listener above
  }

  /* ------------------------------------------------------------------ */
  /*  Initialization                                                     */
  /* ------------------------------------------------------------------ */

  async function init() {
    if (initializing) return;
    initializing = true;
    try {
      await fetchCart();

      // Apply grouping to cart drawer
      const drawerItemList = document.querySelector(
        ".js-cart-drawer .cart-item-list__body, cart-drawer .cart-item-list__body",
      );
      if (drawerItemList) applyGrouping(drawerItemList);

      // Apply grouping to cart page
      const pageItemList = document.querySelector(
        ".cart-form .cart-item-list__body",
      );
      if (pageItemList && !pageItemList.closest("cart-drawer")) {
        applyGrouping(pageItemList);
      }
    } finally {
      initializing = false;
    }
  }

  // Run on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-run after cart drawer opens or cart merges
  document.addEventListener("on:cart-drawer:before-open", () =>
    setTimeout(init, 200),
  );
  document.addEventListener("on:cart:after-merge", () => setTimeout(init, 200));

  // Listen for AJAX cart changes (theme dispatches these)
  document.addEventListener("dispatch:cart-drawer:refresh", () =>
    setTimeout(init, 500),
  );
})();
